import React from 'react';
import styled from 'styled-components';

/**
 * Price ⇄ PnL switch for the chart slot. Lives in the chart header next to the
 * interval select, so the two charts share one frame instead of costing a second
 * panel — the pair screen has no vertical room to spare.
 */

export type ChartView = 'price' | 'pnl';

const Group = styled.div`
  display: inline-flex;
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
`;

const Option = styled.button<{ $active: boolean }>`
  appearance: none;
  border: 0;
  cursor: pointer;
  padding: 3px 10px;
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
  background: ${({ theme, $active }) => ($active ? theme.colors.border.primary : 'transparent')};
  color: ${({ theme, $active }) => ($active ? theme.colors.text.primary : theme.colors.text.secondary)};

  & + & {
    border-left: 1px solid ${({ theme }) => theme.colors.border.primary};
  }

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.border.focus};
    outline-offset: -2px;
  }
`;

interface ChartViewToggleProps {
    value: ChartView;
    onChange: (next: ChartView) => void;
}

const VIEWS: Array<{ key: ChartView; label: string; hint: string }> = [
    { key: 'price', label: 'Price', hint: 'Futures candles' },
    { key: 'pnl', label: 'View', hint: 'Leg notional and the PnL it generates' },
];

const ChartViewToggle: React.FC<ChartViewToggleProps> = ({ value, onChange }) => (
    <Group role="group" aria-label="Chart view">
        {VIEWS.map((v) => (
            <Option
                key={v.key}
                type="button"
                $active={value === v.key}
                aria-pressed={value === v.key}
                title={v.hint}
                onClick={() => onChange(v.key)}
            >
                {v.label}
            </Option>
        ))}
    </Group>
);

export default ChartViewToggle;
